import React, { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { KanbanColumn } from "./KanbanColumn";
import { mondayOf, addDays, filterItems, groupItems } from "./kanbanUtils";
import { SERVICES } from "../../../lib/services";

const MODES = [
  { key: "status", label: "Par statut", hint: "Déplacer une carte change le statut de l'intervention." },
  { key: "easer", label: "Par easer", hint: "Déplacer une carte réaffecte l'intervention à un autre easer (contrôle absence / rayon / chevauchement)." },
  { key: "day", label: "Par jour", hint: "Déplacer une carte replanifie l'intervention à ce jour (heure conservée). Seules les interventions planifiées sont déplaçables." },
];
const PERIODS = [{ key: "week", label: "Semaine affichée" }, { key: "upcoming", label: "À venir" }, { key: "all", label: "Toutes" }];
const selCls = "border border-neutral-300 rounded-md px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:border-brand";

const InterventionKanban = ({ items, team, onMove, onOpen }) => {
  const [mode, setMode] = useState("status");
  const [period, setPeriod] = useState("upcoming");
  const [easer, setEaser] = useState("");
  const [client, setClient] = useState("");
  const [service, setService] = useState("");
  const [monday, setMonday] = useState(() => mondayOf(new Date()));

  const filters = useMemo(() => ({ period: mode === "day" ? "all" : period, easer, client, monday, service }), [mode, period, easer, client, monday, service]);
  const filtered = useMemo(() => filterItems(items, filters), [items, filters]);
  const cols = useMemo(() => groupItems(filtered, mode, team, monday), [filtered, mode, team, monday]);
  const weekLabel = `${monday.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} – ${addDays(monday, 6).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}`;

  return (
    <div data-testid="intervention-kanban">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex rounded-md border border-neutral-300 overflow-hidden" data-testid="kanban-mode-switch">
          {MODES.map((m) => (
            <button key={m.key} onClick={() => setMode(m.key)} data-testid={`kanban-mode-${m.key}`}
              className={`px-3 py-1.5 text-sm font-semibold transition-colors ${mode === m.key ? "bg-brand text-white" : "bg-white text-neutral-600 hover:bg-neutral-50"}`}>{m.label}</button>
          ))}
        </div>
        {(mode === "day" || period === "week") && (
          <div className="flex items-center gap-1" data-testid="kanban-week-nav">
            <button onClick={() => setMonday(addDays(monday, -7))} className="p-1.5 rounded-md border border-neutral-300 hover:bg-neutral-50" data-testid="kanban-week-prev"><ChevronLeft size={14} /></button>
            <button onClick={() => setMonday(mondayOf(new Date()))} className="text-xs font-semibold px-2 py-1.5 rounded-md border border-neutral-300 hover:bg-neutral-50" data-testid="kanban-week-today">{weekLabel}</button>
            <button onClick={() => setMonday(addDays(monday, 7))} className="p-1.5 rounded-md border border-neutral-300 hover:bg-neutral-50" data-testid="kanban-week-next"><ChevronRight size={14} /></button>
          </div>
        )}
        {mode !== "day" && (
          <select className={selCls} value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="kanban-filter-period">
            {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        )}
        {mode !== "easer" && (
          <select className={selCls} value={easer} onChange={(e) => setEaser(e.target.value)} data-testid="kanban-filter-easer">
            <option value="">Tous les easers</option>
            {team.map((t) => <option key={t.login || t.nom} value={t.login || t.nom}>{t.nom}</option>)}
          </select>
        )}
        <select className={selCls} value={service} onChange={(e) => setService(e.target.value)} data-testid="kanban-filter-service">
          <option value="">Tous les services</option>
          {Object.entries(SERVICES).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
        </select>
        <div className="relative">
          <Search size={13} className="absolute left-2 top-2.5 text-neutral-400" />
          <input className={`${selCls} pl-7 w-48`} placeholder="Client, adresse…" value={client} onChange={(e) => setClient(e.target.value)} data-testid="kanban-filter-client" />
        </div>
        <span className="text-xs text-neutral-500 ml-auto" data-testid="kanban-total">{filtered.length} intervention(s)</span>
      </div>
      <p className="text-xs text-neutral-500 mb-3">{MODES.find((m) => m.key === mode).hint}</p>
      <div className="flex gap-3 overflow-x-auto pb-3">
        {cols.map((c) => <KanbanColumn key={c.key} col={c} mode={mode} onOpen={onOpen} onDrop={(id, from, to) => onMove(mode, id, from, to)} />)}
      </div>
    </div>
  );
};

export default InterventionKanban;
